import { useState, useEffect, useCallback } from 'react';
import { Outlet, useNavigate } from 'react-router';
import { useAuth0 } from '@auth0/auth0-react';
import { Loader } from '@dynamon-as/ui';
import DynomiteContext, {
    loadDynomite,
    type DynomiteModule,
} from '@lib/dynomiteContext';
import { addAccessTokenInterceptor } from '@api/dynoape';
import { addAccessTokenInterceptorToDynoLang } from '@api/dynolang';
import {
    useAuthOrganizationValidation,
    useSessionExpired,
} from '@features/auth/hooks';
import { clearAppAuthState, isSessionExpiredError } from '@features/auth/utils';
import { useAuthStore } from '@features/auth/stores';
import type { FC } from 'react';
import type { GetTokenSilentlyOptions } from '@auth0/auth0-react';

const BaseLayout: FC = () => {
    const {
        isLoading: isAuthLoading,
        getAccessTokenSilently,
        loginWithRedirect,
        isAuthenticated,
    } = useAuth0();
    const [dynomite, setDynomite] = useState<DynomiteModule | undefined>();
    const navigate = useNavigate();

    // Full-logout handler used when the Auth0 session has expired
    const { handleSessionExpired } = useSessionExpired();

    // Token getter for the axios interceptors: if the token can no
    // longer be obtained because of an expired session, perform a full
    // logout (the user lands on '/' and logs in again). Only an
    // authenticated user can have an expired session: while
    // unauthenticated, login_required is the normal state and
    // ProtectedRoute already handles the login redirect, so logging out
    // here would loop forever (DYN-3432).
    const getAccessTokenSilentlyWithLogout = useCallback(
        async (options?: GetTokenSilentlyOptions) => {
            try {
                return await getAccessTokenSilently(options);
            } catch (error) {
                if (isAuthenticated && isSessionExpiredError(error)) {
                    handleSessionExpired();
                }
                throw error;
            }
        },
        [getAccessTokenSilently, handleSessionExpired, isAuthenticated],
    );

    // Use hook for organization and token validation
    const {
        isTokenValidationLoading,
        isRedirectingForTokenRenewal,
        validateAuthAndOrg,
    } = useAuthOrganizationValidation({
        getAccessTokenSilently,
        loginWithRedirect,
        onSessionExpired: handleSessionExpired,
        isAuthLoading,
        isAuthenticated,
        isDynomiteLoaded: !!dynomite,
        navigate,
    });

    // Boot guard: once Auth0 has finished loading, if the user is NOT
    // authenticated (e.g. right after a completed logout, which reloads
    // the page on returnTo with the central SSO session already
    // terminated), clear any stale organization context that was
    // persisted to localStorage. This prevents the previously selected
    // organization from silently reappearing on the next login, without
    // ever mutating the store during a logout redirect (which would race
    // with useAuthOrganizationValidation and cause a double logout).
    const selectedOrgId = useAuthStore((state) => state.selectedOrgId);
    const pendingOrgId = useAuthStore((state) => state.pendingOrgId);
    useEffect(() => {
        if (isAuthLoading) {
            return;
        }
        if (!isAuthenticated && (selectedOrgId || pendingOrgId)) {
            clearAppAuthState();
        }
    }, [isAuthLoading, isAuthenticated, selectedOrgId, pendingOrgId]);

    useEffect(() => {
        addAccessTokenInterceptor(getAccessTokenSilentlyWithLogout);
        addAccessTokenInterceptorToDynoLang(getAccessTokenSilentlyWithLogout);
    }, [getAccessTokenSilentlyWithLogout]);

    useEffect(() => {
        const fetchDynomite = async (): Promise<void> => {
            setDynomite(await loadDynomite());
        };
        fetchDynomite();
    }, []);

    // Validate authentication and organization selection when dependencies change
    useEffect(() => {
        validateAuthAndOrg();
    }, [validateAuthAndOrg]);

    // Show loading while:
    // - Auth is loading
    // - Dynomite is loading
    // - Token is being validated
    // - We are about to redirect for token renewal (prevents page flash)
    if (
        isAuthLoading ||
        !dynomite ||
        isTokenValidationLoading ||
        isRedirectingForTokenRenewal
    ) {
        return <Loader isLoading />;
    }

    return (
        <div id="app">
            <DynomiteContext.Provider value={dynomite}>
                <Outlet />
            </DynomiteContext.Provider>
        </div>
    );
};

export default BaseLayout;
